import React, { useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast, ToastContainer } from 'react-toastify';
import { Box, TextField, Button, Typography, FormControl, InputLabel, Select, MenuItem, Tooltip, IconButton, Grid, CircularProgress, Breadcrumbs, Link } from '@mui/material';
import moment from 'moment';
import SearchIcon from '@mui/icons-material/Search';
import { FaPrint, FaFileExport } from 'react-icons/fa';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import axiosInstance from '../../lib/axios';
import ReportTable from '../../atoms/ReportFolder/ReportTable';
import HomeIcon from '@mui/icons-material/Home';

const DailySummaryReport = () => {
    const [rowData, setRowData] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [searchTerms, setSearchTerms] = useState({});
    const [DataForPrint, setDataForPrint] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [actionByOptions, setActionByOptions] = useState([]);
    const [trjOptions, setTrjOtpitons] = useState([]);
    const navigate = useNavigate();
    const gridRef = useRef();
    const contentRef = useRef();

    const [filters, setFilters] = useState({
        SearchFrom: moment().format('YYYY-MM-DD'),
        toDate: moment().format('YYYY-MM-DD'),
        operation: 'All',
        actionBy: 'All',
        transaction: 'All',
    });

    const handlePrint = useReactToPrint({ contentRef });

    // Column definitions for AG Grid
    const colDefs = [
        { field: 'ActionDt', headerName: 'Date', width: 200 },
        { field: 'TransName', headerName: 'Transactions', width: 400 },
        { field: 'Name', headerName: 'ActionBy', width: 400 },
        {
            field: 'Operation',
            headerName: 'Action',
            width: 300,
            cellStyle: params => {
                if (params.value === 'Added') {
                    return { color: '#155724', fontWeight: 'bold' };
                } else if (params.value === 'Edited') {
                    return { color: 'purple', fontWeight: 'bold' };
                } else if (params.value === 'Deleted') {
                    return { color: '#eb0d23ff', fontWeight: 'bold' };
                }
                return null;
            }
        },
        { field: 'RecHisId', headerName: 'Entries', width: 150 },
    ];

    // Transform colDefs to match ReportTable's expected format
    const printColumns = colDefs.map(col => ({
        id: col.field,
        label: col.headerName,
        minWidth: col.minWidth,
        maxWidth: col.maxWidth,
        align: col.align || 'left',
    }));

    // Default column settings
    const defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        // enableRowGroup: true,
        width: 100,
    };

    useEffect(() => {
        fetchActionByData();
        fetchAuditData();
        fetchTransaction();
    }, []);

    // Fetch audit data
    const fetchAuditData = async () => {
        setIsLoading(true);
        try {
            const { SearchFrom, toDate, actionBy, operation } = filters;
            const createdDate = SearchFrom || moment().format('YYYY-MM-DD');
            const modifiedDate = toDate || moment().format('YYYY-MM-DD');

            const response = await axiosInstance.post('RecHistory/GetRecordHistoryDashBoard', {
                start: 1,
                PageSize: 1,
                SearchText: '',
                ColText: '',
                CreatedDate: createdDate,
                ModifiedDate: modifiedDate,
                Cwhere: '',
                Flag: '',
            });

            const { data: { Status, Data } } = response;
            if (Status === 0) {
                const formattedData = Data.map((audit) => ({
                    ...audit,
                    ActionDt: moment(audit.ActionDt).format('DD-MM-YYYY'),
                    Operation: audit.Operation === 'A' ? 'Added' : audit.Operation === 'E' ? 'Edited' : audit.Operation === 'D' ? 'Deleted' : audit.Operation,
                }));
                setRowData(formattedData);
                setFilteredRows(formattedData);
                setDataForPrint(response.data);
            } else {
                toast.info(response.data.Message + ' for the given date.');
            }
        } catch (error) {
            toast.error('Error fetching Audit Report data: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch action by options
    const fetchActionByData = async () => {
        try {
            const response = await axiosInstance.get('UserMst/GetMakeDropDown');
            const { data: { Status, Data } } = response;
            if (Status === 0) {
                const options = Data.map((item) => ({
                    value: item.Name,
                    label: item.Name,
                }));
                setActionByOptions([{ value: 'All', label: 'All' }, ...options]);
            } else {
                toast.error('Error fetching Action By data: ' + response.data.Message);
            }
        } catch (error) {
            toast.error('Error fetching Action By data: ' + error.message);
        }
    };

    useEffect(() => {
        if (trjOptions.length > 0 && filters.transaction === '') {
            setFilters((prev) => ({ ...prev, transaction: trjOptions[0].value }));
        }
    }, [trjOptions]);

    // Fetch Transaction options data
    const fetchTransaction = async () => {
        try {
            const response = await axiosInstance.post("RecHistory/GetRecordHistoryDashBoard", {
                start: 1,
                PageSize: 1,
                SearchText: "",
                ColText: "",
                CreatedDate: "2025-07-11",
                ModifiedDate: "2025-07-11",
                Cwhere: "",
                Flag: "trj"
            });
            const { data: { Status, Data } } = response;
            if (Status === 0) {
                const trjDataOption = Data.map((item) => ({
                    value: item.TransName,
                    label: item.TransName,
                }));
                setTrjOtpitons([{ value: 'All', label: 'All' }, ...trjDataOption]);
            } else {
                toast.error("Error while fetching data");
            }
        } catch (error) {
            toast.error("Error while fetching transaction");
        }
    };

    // Handle filter changes
    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    // Handle search
    const handleSearch = () => {
        fetchAuditData();
    };

    // Filter rows based on search terms and filters
    useEffect(() => {
        const fromDate = filters.SearchFrom ? moment(filters.SearchFrom).startOf('day') : moment().startOf('day');
        const toDate = filters.toDate ? moment(filters.toDate).endOf('day') : moment().endOf('day');

        const filtered = rowData.filter((row) => {
            const actionDate = moment(row.ActionDt, 'DD-MM-YYYY - HH:mm');
            return (
                Object.entries(searchTerms).every(([columnId, term]) =>
                    !term || row[columnId]?.toString().toLowerCase().includes(term.toLowerCase())
                ) &&
                (filters.actionBy === 'All' || row.Name?.toLowerCase() === filters.actionBy.toLowerCase()) &&
                (filters.transaction === 'All' || row.TransName?.toLowerCase() === filters.transaction.toLowerCase()) &&
                actionDate.isSameOrAfter(fromDate) &&
                actionDate.isSameOrBefore(toDate) &&
                (filters.operation === 'All' ||
                    (filters.operation === 'Add' && row.Operation === 'Added') ||
                    (filters.operation === 'Edit' && row.Operation === 'Edited') ||
                    (filters.operation === 'Delete' && row.Operation === 'Deleted'))
            );
        });

        setFilteredRows(filtered);
    }, [searchTerms, rowData, filters]);

    // Handle search change for AG Grid floating filters
    const handleSearchChange = (columnId, value) => {
        setSearchTerms((prev) => ({ ...prev, [columnId]: value }));
        gridRef.current.api.setQuickFilter(value);
    };

    // Navigation handlers
    const handleHomeClick = () => navigate('/reports/dailysummaryreport');

    const handleExport = () => {
        if (gridRef.current && gridRef.current.api) {
            gridRef.current.api.exportDataAsCsv({
                fileName: `Audit_Trail_Report_${moment().format('YYYYMMDD')}.csv`,
            });
            toast.success('Data exported successfully');
        } else {
            toast.error('Grid is not initialized');
        }
    };

    // Filter JSX components
    const FromDateFilter = (
        <TextField
            label="From Date"
            type="date"
            variant="outlined"
            size="small"
            value={filters.SearchFrom}
            onChange={(e) => handleFilterChange('SearchFrom', e.target.value)}
            sx={{ width: '130px' }}
            InputLabelProps={{ shrink: true }}
        />
    );

    const ToDateFilter = (
        <TextField
            label="To Date"
            type="date"
            variant="outlined"
            size="small"
            value={filters.toDate}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
            sx={{ width: '130px' }}
            InputLabelProps={{ shrink: true }}
        />
    );

    const ActionTransaction = (
        <FormControl size="small" sx={{ width: 130 }}>
            <InputLabel>Transaction</InputLabel>
            <Select
                value={filters.transaction}
                onChange={(e) => handleFilterChange('transaction', e.target.value)}
                label="Transaction"
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 250,
                            overflowY: 'auto',
                        },
                    },

                    MenuListProps: {
                        sx: {
                            '& .MuiMenuItem-root': {
                                paddingTop: 0,
                                paddingBottom: "1px",
                            },
                        },
                    },
                }}
            >
                {trjOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const ActionByFilter = (
        <FormControl size="small" sx={{ width: 130 }}>
            <InputLabel>Action By</InputLabel>
            <Select
                value={filters.actionBy}
                onChange={(e) => handleFilterChange('actionBy', e.target.value)}
                label="Action By"
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 200,
                            overflowY: 'auto',
                        },
                    },

                    MenuListProps: {
                        sx: {
                            '& .MuiMenuItem-root': {
                                paddingTop: 0,
                                paddingBottom: "1px",
                            },
                        },
                    },
                }}
            >
                {actionByOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const ActionFilter = (
        <FormControl size="small" sx={{ width: 130 }}>
            <InputLabel>Action</InputLabel>
            <Select
                value={filters.operation}
                onChange={(e) => handleFilterChange('operation', e.target.value)}
                label="Action"
            >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Add">Add</MenuItem>
                <MenuItem value="Edit">Edit</MenuItem>
                <MenuItem value="Delete">Delete</MenuItem>
            </Select>
        </FormControl>
    );

    return (
        <Box sx={{
            width: '100%',
            height: '90vh',
            padding: '10px',
            backgroundColor: 'rgb(249, 249, 249)',
            position: 'relative'
        }}>
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "8px 16px", sm: "12px 16px" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "97%",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: 2,
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={() => navigate('/reports/dailysummaryreport')}
                    >
                        Report
                    </Link>

                    <Typography color="text.primary" sx={{ fontWeight: "500" }}>
                        Daily Summary Report
                    </Typography>
                </Breadcrumbs>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item>{FromDateFilter}</Grid>
                        <Grid item>{ToDateFilter}</Grid>
                        <Grid item>{ActionTransaction}</Grid>
                        <Grid item>{ActionByFilter}</Grid>
                        <Grid item>{ActionFilter}</Grid>
                        <Grid item sx={{ display: 'flex', gap: '10px' }}>
                            <Tooltip title="Search" arrow>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleSearch}
                                    sx={{
                                        backgroundColor: '#635bff',
                                        color: 'white',
                                        borderRadius: '20px',
                                        minWidth: '10px',
                                        '&:hover': { backgroundColor: '#dddbff', color: '#635bff' },
                                    }}
                                >
                                    <SearchIcon sx={{ fontSize: '25px', }} />
                                </Button>
                            </Tooltip>
                            <Tooltip title="Print" arrow>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handlePrint}
                                    sx={{
                                        backgroundColor: '#635bff',
                                        color: 'white',
                                        borderRadius: '20px',
                                        minWidth: '18px',
                                        '&:hover': { backgroundColor: '#dddbff', color: '#635bff' },
                                    }}
                                >
                                    <FaPrint style={{ fontSize: '20px', }} />
                                </Button>
                            </Tooltip>
                            <Tooltip title="Export" arrow>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleExport}
                                    sx={{
                                        backgroundColor: '#635bff',
                                        color: 'white',
                                        borderRadius: '20px',
                                        minWidth: '18px',
                                        '&:hover': { backgroundColor: '#dddbff', color: '#635bff' },
                                    }}
                                >
                                    <FaFileExport style={{ fontSize: '18px', }} />
                                </Button>
                            </Tooltip>
                            <Tooltip title="Main Page" arrow>
                                <IconButton
                                    onClick={handleHomeClick}
                                    sx={{
                                        backgroundColor: '#635bff',
                                        borderRadius: '50%',
                                        width: 40,
                                        height: 40,
                                        '&:hover': { backgroundColor: '#635bff' },
                                        boxShadow: 3,
                                    }}
                                >
                                    <ArrowBackIcon sx={{ color: 'white' }} />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            <Box sx={{ width: '100%', height: '90vh', px: '20px', backgroundColor: 'rgb(249, 249, 249)', position: 'relative' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ToastContainer />
                    {/* <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>{FromDateFilter}</Grid>
                            <Grid item>{ToDateFilter}</Grid>
                            <Grid item>{ActionTransaction}</Grid>
                            <Grid item>{ActionByFilter}</Grid>
                            <Grid item>{ActionFilter}</Grid>
                            <Grid item sx={{ display: 'flex', gap: '10px' }}>
                                <Tooltip title="Search" arrow>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleSearch}
                                        sx={{
                                            backgroundColor: '#635bff',
                                            color: 'white',
                                            borderRadius: '20px',
                                            minWidth: '10px',
                                            '&:hover': { backgroundColor: '#dddbff', color: '#635bff' },
                                        }}
                                    >
                                        <SearchIcon sx={{ fontSize: '25px', }} />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Print" arrow>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handlePrint}
                                        sx={{
                                            backgroundColor: '#635bff',
                                            color: 'white',
                                            borderRadius: '20px',
                                            minWidth: '20px',
                                            '&:hover': { backgroundColor: '#dddbff', color: '#635bff' },
                                        }}
                                    >
                                        <FaPrint style={{ fontSize: '20px', }} />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Export" arrow>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleExport}
                                        sx={{
                                            backgroundColor: '#635bff',
                                            color: 'white',
                                            borderRadius: '20px',
                                            minWidth: '20px',
                                            '&:hover': { backgroundColor: '#dddbff', color: '#635bff' },
                                        }}
                                    >
                                        <FaFileExport style={{ fontSize: '18px', }} />
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Main Page" arrow>
                                    <IconButton
                                        onClick={handleHomeClick}
                                        sx={{
                                            backgroundColor: '#635bff',
                                            borderRadius: '50%',
                                            width: 40,
                                            height: 40,
                                            '&:hover': { backgroundColor: '#635bff' },
                                            boxShadow: 3,
                                        }}
                                    >
                                        <ArrowBackIcon sx={{ color: 'white' }} />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Box> */}
                </Box>

                <Box sx={{ height: 'calc(90% - 56px)', width: '100%', position: 'relative' }}>
                    {isLoading ? (
                        <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            textAlign: 'center'
                        }}>
                            <svg width={0} height={0}>
                                <defs>
                                    <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop offset="0%" stopColor="#e01cd5" />
                                        <stop offset="100%" stopColor="#1CB5E0" />
                                    </linearGradient>
                                </defs>
                            </svg>
                            <CircularProgress size="3rem" sx={{ 'svg circle': { stroke: 'url(#my_gradient)' } }} />
                            <Typography sx={{ marginTop: 2, color: '#1976d2', fontWeight: 'bold' }}>Loading data...</Typography>
                        </Box>
                    ) : (
                        <Box className="table-grid ag-theme-alpine" sx={{ height: '100%', width: '100%' }}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={filteredRows}
                                columnDefs={colDefs}
                                defaultColDef={defaultColDef}
                                pagination={true}
                                paginationPageSize={100}
                                rowSelection="multiple"
                                paginationPageSizeSelector={[50, 100, 500, 1000, 5000]}
                                suppressRowClickSelection={true}
                                rowHeight={20}
                                headerHeight={30}
                                floatingFiltersHeight={28}
                                overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rows to show</span>'}
                            />
                        </Box>
                    )}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 10,
                            left: 20,
                            fontSize: '14px',
                            color: 'black',
                        }}
                    >
                        Total Records: <b>{rowData.length}</b>
                    </Box>
                </Box>

                {/* Print Section */}
                {DataForPrint && Object.keys(DataForPrint).length > 0 && (
                    <div style={{ display: 'none' }}>
                        <ReportTable
                            columns={printColumns}
                            rows={filteredRows}
                            title="Transaction wise Daily Report"
                            customStyles={{ backgroundColor: '#f5f5f5' }}
                            contentRef={contentRef}
                        />
                    </div>
                )}
            </Box>
        </Box>
    );
};

export default DailySummaryReport;